// Smart Platform Redirect — Client-Script für die Web-App (my-within.app).
// Leitet mobile Besucher (Android/iOS) auf konfigurierten PUBLIC-Pfaden an den
// passenden App Store. Bots/Desktop/Tablet werden NIE umgeleitet. my-within.com
// nutzt separat die Astro-Landing mit demselben Verhalten.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { captureEvent } from "@/lib/analytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DISMISS_KEY = "mw_sr_dismissed";
const DISMISS_MAX_MS = 60 * 60 * 1000; // 1h: nach Store-Hop keine Schleife

const BOT_FRAGMENTS = [
  "bot", "spider", "crawl", "googlebot", "bingbot", "duckduckbot", "baiduspider",
  "yandex", "applebot", "gptbot", "chatgpt", "oai-searchbot", "claudebot", "claude-web",
  "anthropic", "perplexity", "slurp", "facebookexternalhit", "linkedinbot", "twitterbot",
  "whatsapp", "telegrambot", "discordbot", "embedly",
];

function detectPlatform() {
  const ua = navigator.userAgent || "";
  const low = ua.toLowerCase();
  if (BOT_FRAGMENTS.some((f) => low.includes(f))) return "bot";
  if (/iphone|ipod/i.test(ua)) return "ios";
  if (/ipad/i.test(ua)) return "tablet"; // Tablet → kein Redirect
  if (/android/i.test(ua)) return /mobile/i.test(ua) ? "android" : "tablet";
  return "desktop";
}

function pathMatches(current, patterns) {
  return (patterns || []).some((p) => {
    if (!p) return false;
    if (p.includes("*")) {
      const base = p.slice(0, p.indexOf("*")).replace(/\/$/, "");
      return current === base || current.startsWith(base + "/") || current.startsWith(base);
    }
    const norm = p.length > 1 ? p.replace(/\/$/, "") : p;
    const cur = current.length > 1 ? current.replace(/\/$/, "") : current;
    return cur === norm;
  });
}

async function evaluate(pathname, search) {
  if (!BACKEND || typeof window === "undefined") return;
  const params = new URLSearchParams(search);
  if (params.get("noredirect") === "1") {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }
    return;
  }
  try {
    const last = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (last && Date.now() - last < DISMISS_MAX_MS) return;
  } catch { /* noop */ }

  const platform = detectPlatform();
  if (platform === "bot" || platform === "desktop" || platform === "tablet") return;

  let cfg;
  try {
    const res = await fetch(`${BACKEND}/api/go/config`, { credentials: "omit" });
    if (!res.ok) return;
    cfg = await res.json();
  } catch {
    return;
  }
  if (!cfg || !cfg.enabled) return;
  if (!pathMatches(pathname, cfg.paths)) return;
  if (platform === "ios" && !cfg.ios_url) return; // iOS ohne Store-URL: bleiben
  if (platform === "android" && !cfg.android_url) return;

  // Carry-over der UTM-Parameter + Pfad an den Backend-Hop (loggt + 302).
  const forward = new URLSearchParams();
  ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "src"].forEach((k) => {
    const v = params.get(k);
    if (v) forward.set(k, v);
  });
  forward.set("path", pathname);

  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* noop */ }

  // Spiegel den Klick an PostHog (gleiche distinct_id wie der $pageview-Besuch)
  // → ermöglicht den Funnel Besuch → Store-Hop. keepalive sorgt dafür, dass das
  // Event trotz sofortiger Weiterleitung noch rausgeht.
  captureEvent("smart_redirect_click", {
    platform,
    store: platform,
    sr_path: pathname,
    utm_source: params.get("utm_source") || params.get("src") || "",
    utm_medium: params.get("utm_medium") || "",
    utm_campaign: params.get("utm_campaign") || "",
    utm_content: params.get("utm_content") || "",
    utm_term: params.get("utm_term") || "",
    is_instagram: /instagram/i.test(navigator.userAgent || ""),
  });

  window.location.replace(`${BACKEND}/api/go/${platform}?${forward.toString()}`);
}

export function SmartRedirect() {
  const location = useLocation();
  useEffect(() => {
    evaluate(location.pathname, location.search);
  }, [location.pathname, location.search]);
  return null;
}
